import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuoteLeft } from '@fortawesome/free-solid-svg-icons'
import { ArticleSchule } from "./ArticleSchule";
import Newsletter from "./Newsletter";
import { ArticleAusbildung } from "./ArticleAusbildung"
import { ArticleBiodanza } from "./ArticleBiodanza";
import { useSiteSettings } from "../sanity/useSiteSettings";
import { LoadingSpinner } from "../UI/LoadingSpinner";
import { urlFor } from "../sanity/imageUrl";
import { useEffect, useMemo, useState } from "react";
import { Reveal } from "../UI/Reveal";
import headerVideoMp4 from "../imgs/background.mp4";
import headerPosterJpg from "../imgs/background.jpg";
import { FancyLoader } from "../UI/FancyLoader";
import { createPortal } from "react-dom";

export const HomePage = () => {
    const { data, loading } = useSiteSettings();
    const quote = data?.quote || "Biodanza ist die Kunst, die das Leben feiert und die Liebe zum Leben weckt.";
    const quoteAuthor = data?.quoteAuthor || "Rolando Toro";

    // Header background: use a professional, responsive looping video.
    // We still support a poster image (Sanity header image if present, else local JPG)
    // and we respect reduced-motion (falls back to poster image).
    const headerBgUrl = useMemo(() => {
        const img = data?.backgroundImages?.header
        return img ? urlFor(img) : null
    }, [data])
    const [headerBgReady, setHeaderBgReady] = useState(false)
    const [useVideo, setUseVideo] = useState(true)
    const [videoReady, setVideoReady] = useState(false)

    useEffect(() => {
        const reduce =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        setUseVideo(!reduce)
    }, [])

    useEffect(() => {
        if (loading) {
            setHeaderBgReady(false)
            setVideoReady(false)
            return
        }
        // If we are using the video, DON'T block the first render.
        // Show the poster/background immediately and let the video fade in once ready.
        if (useVideo) {
            setHeaderBgReady(true)
            setVideoReady(false)
            return
        }
        // Image fallback path (reduced-motion or video unsupported)
        if (!headerBgUrl) {
            setHeaderBgReady(true)
            return
        }

        let cancelled = false
        setHeaderBgReady(false)
        const img = new Image()
        img.onload = () => { if (!cancelled) setHeaderBgReady(true) }
        img.onerror = () => { if (!cancelled) setHeaderBgReady(true) }
        img.src = headerBgUrl
        return () => { cancelled = true }
    }, [loading, headerBgUrl, useVideo])

    // Fullscreen loader only for initial site settings fetch (not for video buffering)
    const showFullScreenLoader = loading
    const showHeaderLoader = !useVideo && !headerBgReady
    return (
        <div>

            {showFullScreenLoader && typeof document !== "undefined"
                ? createPortal(
                    <div className="home-loader-overlay">
                        <FancyLoader label="Lädt…" />
                    </div>,
                    document.body
                )
                : null}

            <div className={`header ${useVideo ? 'header--video' : ''} ${showHeaderLoader ? 'header--loading' : ''}`.trim()}>
                {useVideo ? (
                    <video
                        className={`header-video ${videoReady ? 'header-video--ready' : ''}`.trim()}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={headerBgUrl || headerPosterJpg}
                        onCanPlay={() => setVideoReady(true)}
                        onError={() => setVideoReady(false)}
                    >
                        <source src={headerVideoMp4} type="video/mp4" />
                    </video>
                ) : null}
                {showHeaderLoader ? <LoadingSpinner label="Lädt…" size="lg" /> : null}
            </div>
            <Reveal>
                <ArticleBiodanza />
            </Reveal>

            <Reveal delay={0.05}>
                <div className="quote ">
                    <div className="quote-inner" >
                        <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" /> <br />
                        {loading ? (
                            <LoadingSpinner label="Lädt…" />
                        ) : (
                            <>
                                {quote}
                                <p className="quote-author">
                                    <em>- {quoteAuthor}</em>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.08}>
                <ArticleSchule />
            </Reveal>

            <Reveal delay={0.1}>
                <ArticleAusbildung />
            </Reveal>

            <Reveal delay={0.12}>
                <Newsletter />
            </Reveal>
        </div>
    )
}